import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  getUsuarios,
  updateUser,
  crearUsuario,
  reenviarInvitacion,
  enviarResetUsuario,
} from "../../../services/usuarios_tienda/usuarios";
import UserFormModal from "./UserFormModal";
import { IconEdit } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";
import "./UsersPage.css";

// Mapeo de estados (coincide con el backend): 0 Inactivo, 1 Activo, 2 Pendiente, 3 Bloqueado.
const ESTADOS = {
  0: { label: "Inactivo", cls: "inactive" },
  1: { label: "Activo", cls: "active" },
  2: { label: "Invitación pendiente", cls: "pending" },
  3: { label: "Bloqueado", cls: "blocked" },
};

function UsuariosPage() {
  const [usuarios, setUsuarios] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);
  const showAlert = useCallback((type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 3500);
  }, []);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [searchDebounced]);

  const cargarUsuarios = useCallback(async () => {
    try {

      const res = await getUsuarios(page, 20, "", searchDebounced);

      setUsuarios(Array.isArray(res?.data) ? res.data : []);
      setTotalPages(Number(res?.totalPages) || 1);
    } catch (error) {
      console.error("Error cargando usuarios:", error);
      showAlert("error", error?.message || "No se pudieron cargar los usuarios.");
      setUsuarios([]);
      setTotalPages(1);
    }
  }, [page, searchDebounced, showAlert]);

  useEffect(() => {
    cargarUsuarios();
  }, [cargarUsuarios]);

  const openCreateModal = () => {
    setSelectedUser(null);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleUpdate = async (id, formData) => {
    try {
      const res = await updateUser(id, formData);
      showAlert("success", res?.message || "Usuario actualizado correctamente.");
      await cargarUsuarios();
      setShowModal(false);
    } catch (err) {
      console.error("Error updating user:", err);
      throw err;
    }
  };

  const handleCreate = async (formData) => {
    try {
      const res = await crearUsuario(formData);
      showAlert(
        res?.emailEnviado === false ? "error" : "success",
        res?.message || "Invitación enviada correctamente."
      );
      await cargarUsuarios();
      setShowModal(false);
    } catch (err) {
      console.error("Error creating user:", err);
      throw err;
    }
  };

  const [busyId, setBusyId] = useState(null);

  const handleResend = async (id) => {
    setBusyId(id);
    try {
      const res = await reenviarInvitacion(id);
      showAlert(res?.emailEnviado === false ? "error" : "success", res?.message || "Invitación reenviada.");
      await cargarUsuarios();
    } catch (err) {
      showAlert("error", err?.message || "No se pudo reenviar la invitación.");
    } finally {
      setBusyId(null);
    }
  };

  const handleSendReset = async (id) => {
    setBusyId(id);
    try {
      const res = await enviarResetUsuario(id);
      showAlert(res?.emailEnviado === false ? "error" : "success", res?.message || "Enlace de restablecimiento enviado.");
    } catch (err) {
      showAlert("error", err?.message || "No se pudo enviar el enlace.");
    } finally {
      setBusyId(null);
    }
  };

  const nextPage = () => page < totalPages && setPage((p) => p + 1);
  const prevPage = () => page > 1 && setPage((p) => p - 1);

  const goToPage = (n) => {
    if (n < 1 || n > totalPages || n === page) return;
    setPage(n);
  };

  const visiblePages = useMemo(() => {
    const count = 3;
    if (totalPages <= count) return Array.from({ length: totalPages }, (_, i) => i + 1);
    let s = Math.max(1, page - Math.floor(count / 2));
    let e = s + count - 1;
    if (e > totalPages) {
      e = totalPages;
      s = e - count + 1;
    }
    return Array.from({ length: e - s + 1 }, (_, i) => s + i);
  }, [page, totalPages]);

  const canCreate = useMemo(() => hasPerm("create_user"), []);
  const canUpdate = useMemo(() => hasPerm("update_user"), []);

  return (
    <div className="tienda-page-scope">

      {alert && (
        <div className={`omx-alert omx-alert--${alert.type}`}>
          <span className="omx-alert__msg">{alert.message}</span>
          <button className="omx-alert__x" onClick={() => setAlert(null)}>
            ✕
          </button>
        </div>
      )}

      <div className="header">
        <div className="methods-header__row">
          <h2 className="title">Gestión de usuarios</h2>

          {canCreate && (
            <button type="button" className="methods-btn-create" onClick={openCreateModal}>
              Invitar usuario
            </button>
          )}
        </div>

        <p className="page-lead">
          Invita y administra a los usuarios de tu tienda, sus roles y su acceso.
        </p>

        <div className="top-bar">
          <input
            type="text"
            placeholder="Buscar por correo..."
            className="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="table-card">
        <div className="table-card-scroll">
        <table className="modern-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Apellido</th>
              <th>Correo</th>
              <th>Rol</th>
              <th>Estado</th>
              <th>Fecha</th>
              <th style={{ textAlign: "right" }}>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {usuarios.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  No se encontraron usuarios
                </td>
              </tr>
            ) : (
              usuarios.map((u) => (
                <tr key={u.usuario_tienda_id}>
                  <td>{u.usuario_tienda_id}</td>
                  <td>{u.nombre}</td>
                  <td>{u.apellido}</td>
                  <td>{u.correo}</td>
                  <td>{u.rol_nombre}</td>
                  <td>
                    <span className={`status ${(ESTADOS[u.estado] || ESTADOS[0]).cls}`}>
                      {(ESTADOS[u.estado] || ESTADOS[0]).label}
                    </span>
                  </td>
                  <td>{u.fecha_creado?.slice(0, 10)}</td>

                  <td>
                    <div className="actions">
                      {canUpdate && u.estado === 2 && (
                        <button
                          className="link-action"
                          onClick={() => handleResend(u.usuario_tienda_id)}
                          disabled={busyId === u.usuario_tienda_id}
                          title="Reenviar invitación"
                        >
                          {busyId === u.usuario_tienda_id ? "Enviando..." : "Reenviar invitación"}
                        </button>
                      )}

                      {canUpdate && u.estado === 1 && (
                        <button
                          className="link-action"
                          onClick={() => handleSendReset(u.usuario_tienda_id)}
                          disabled={busyId === u.usuario_tienda_id}
                          title="Enviar enlace para restablecer contraseña"
                        >
                          {busyId === u.usuario_tienda_id ? "Enviando..." : "Restablecer contraseña"}
                        </button>
                      )}

                      {canUpdate && (
                        <span
                          className="action-icon edit"
                          onClick={() => openEditModal(u)}
                          title="Editar usuario"
                        >
                          {IconEdit}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {showModal && (
        <UserFormModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          userData={selectedUser}
          onSubmit={(formData) =>
            selectedUser
              ? handleUpdate(selectedUser.usuario_tienda_id, formData)
              : handleCreate(formData)
          }
        />
      )}

      {usuarios.length > 0 && (
        <div className="pagination">
          <div className="pagination__center">
            <div className="pagination__nav" aria-label="Paginación de usuarios">
              <button
                type="button"
                className="pagination__arrow"
                onClick={prevPage}
                disabled={page === 1}
                aria-label="Página anterior"
              >
                ‹
              </button>

              <div className="pagination__pages">
                {visiblePages.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`pagination__page${n === page ? " is-active" : ""}`}
                    onClick={() => goToPage(n)}
                    aria-label={`Ir a página ${n}`}
                    aria-current={n === page ? "page" : undefined}
                  >
                    {n}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="pagination__arrow"
                onClick={nextPage}
                disabled={page === totalPages}
                aria-label="Página siguiente"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsuariosPage;
